import { PreviewType } from 'js/file_viewer/core/types/preview_type';
// For archive, text/html, video/audio, and excel previews the file limit is the same
// for all file types of that preview type, but for the remaining types it's a
// bit more of a jumbled mess so we just explicitly list the size limits by file
// extension based on the rivier config:
// go/src/dropbox/riviera/plugins/plugins.yaml
// and cross referenced with the help article on file size limits:
// https://help.dropbox.com/es-es/files-folders/sort-preview/file-types-that-preview
const MB = 1024 * 1024;
export const GB_1 = 1024 * MB;
const IMAGE_DEFAULT_SIZE_LIMIT = 500 * MB;
const DEFAULT_OTHER_SIZE_LIMITS = {
    '.3fr': 200 * MB,
    '.ai': 256 * MB,
    '.arw': 200 * MB,
    '.avif': IMAGE_DEFAULT_SIZE_LIMIT,
    '.azw4': 30 * MB,
    '.binder': 200 * MB,
    '.bmp': IMAGE_DEFAULT_SIZE_LIMIT,
    '.cr2': 200 * MB,
    '.cr3': 200 * MB,
    '.crw': 200 * MB,
    '.csv': 40 * MB,
    '.dcr': 200 * MB,
    '.dcs': 200 * MB,
    '.djvu': 30 * MB,
    '.dng': 200 * MB,
    '.doc': 160 * MB,
    '.docm': 160 * MB,
    '.docx': 160 * MB,
    '.dot': 160 * MB,
    '.dotx': 160 * MB,
    '.dwg': 100 * MB,
    '.eps': 256 * MB,
    '.epub': 30 * MB,
    '.erf': 200 * MB,
    '.gdoc': 160 * MB,
    '.gif': IMAGE_DEFAULT_SIZE_LIMIT,
    '.glsides': 288 * MB,
    '.gsheet': 40 * MB,
    '.heic': 100 * MB,
    '.j2c': IMAGE_DEFAULT_SIZE_LIMIT,
    '.j2k': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jp2': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpc': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpeg': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpf': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpg': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpg2': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpm': IMAGE_DEFAULT_SIZE_LIMIT,
    '.jpx': IMAGE_DEFAULT_SIZE_LIMIT,
    '.kdc': 200 * MB,
    '.key': GB_1,
    '.kth': GB_1,
    '.mef': 200 * MB,
    '.mobi': 30 * MB,
    '.mos': 200 * MB,
    '.mrw': 200 * MB,
    '.nef': 200 * MB,
    '.nrw': 200 * MB,
    '.numbers': GB_1,
    '.odg': 160 * MB,
    '.odp': 160 * MB,
    '.ods': 40 * MB,
    '.odt': 160 * MB,
    '.orf': 200 * MB,
    '.otf': 32 * MB,
    '.pages': GB_1,
    '.paper': 200 * MB,
    '.papert': 200 * MB,
    '.pdf': 500 * MB,
    '.pef': 200 * MB,
    '.png': IMAGE_DEFAULT_SIZE_LIMIT,
    '.ppm': IMAGE_DEFAULT_SIZE_LIMIT,
    '.pps': 288 * MB,
    '.ppsm': 288 * MB,
    '.ppsx': 288 * MB,
    '.ppt': 288 * MB,
    '.pptm': 288 * MB,
    '.pptx': 288 * MB,
    '.ps': 256 * MB,
    '.psd': GB_1,
    '.r3d': 200 * MB,
    '.raf': 200 * MB,
    '.rtf': 160 * MB,
    '.rw2': 200 * MB,
    '.rwl': 200 * MB,
    '.sketch': GB_1,
    '.sr2': 200 * MB,
    '.svg': 48 * MB,
    '.svgz': 48 * MB,
    '.tga': IMAGE_DEFAULT_SIZE_LIMIT,
    '.tif': 800 * MB,
    '.tiff': 800 * MB,
    '.ttf': 32 * MB,
    '.vtt': 100 * MB,
    '.wbmp': IMAGE_DEFAULT_SIZE_LIMIT,
    '.webp': IMAGE_DEFAULT_SIZE_LIMIT,
    '.wpd': 160 * MB,
    '.x3f': 200 * MB,
    '.xd': GB_1,
};
export const AUDIO_VIDEO_EDITING_DURATION_MINS_LIMIT = 10;
export const AUDIO_VIDEO_EDITING_SIZE_LIMIT = GB_1;
export const DEFAULT_CONFIG = {
    other_size_limits: DEFAULT_OTHER_SIZE_LIMITS,
    archive_size_limit: 512 * MB,
    text_size_limit: 8 * MB,
    linkfile_size_limit: 10 * MB,
    excel_size_limit: 40 * MB,
    enable_fullscreen: false,
    file_flipping: false,
    keyboard_shortcuts: false,
    autohide_video_controls: false,
    cloud_doc_preview_mode: 'open_in_button',
    mobile_web: false,
    preview_types: {
        [PreviewType.SsrDoc]: { scaleToFit: 'width_and_height' },
    },
    videojs_preload: true,
    initial_video_bandwidth: undefined,
    previews_exp_enable_mux_data: false,
    // END EXPERIMENT (acheung): previews_exp_enable_mux_data
    previews_transcription_translation: 'OFF', // EXPERIMENT (aryatandon): previews_transcription_translation
    preview_collection_slideshow: 'OFF', // EXPERIMENT (dipoarowona): preview_collection_slideshow
    previews_open_in_m2: 'OFF', // EXPERIMENT (calvinlee): previews_open_in_m2
    previews_videojs_touch_events: false,
};
export const DEFAULT_FILE_CONFIG = {
    download_button: undefined,
    enable_print: false,
    enable_view_on_dropbox_button: false,
    canEdit: false,
};
function getFileSizeLimit(fileExtension, previewType, config) {
    const otherSizeLimit = config.other_size_limits[fileExtension];
    if (otherSizeLimit) {
        return otherSizeLimit;
    }
    switch (previewType) {
        case PreviewType.Archive:
            return config.archive_size_limit;
        case PreviewType.Text:
            return config.text_size_limit;
        case PreviewType.Linkfile:
            return config.linkfile_size_limit;
        case PreviewType.Excel:
            return config.excel_size_limit;
    }
    return 0;
}
/**
 * Check max file size we supported
 * @param fileExtension File type extension
 * @param fileSize File size in bytes
 * @param config file size limit config
 */
export function fileSizeSupported(fileExtension, fileSize, previewType, config) {
    if (!isFinite(fileSize)) {
        return true;
    }
    const fileSizeLimit = getFileSizeLimit(fileExtension, previewType, config);
    return !fileSizeLimit || fileSize <= fileSizeLimit;
}
//# sourceMappingURL=config.preenvesnextjs.map
